import { ApolloLink, createHttpLink, from } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/browser';
import { message } from 'antd';
import { isObject } from 'lodash';
import { PLATFORM_TYPE, ROUTES, TOKEN } from './common/constants';
import history from './historyData';

let disableToastTimeout = null;

const SILENT_MESSAGE_OPERATIONS = [
  'downloadInventoryUnitQrCodesAdmin',
  'downloadInventoryUnitQrCodes',
  'checkInventoryUnitAssignment',
  'packInventoryUnit',
  'markInventoryUnitDamaged',
  'previewInventoryUnitInspection',
  'confirmReturnOrderV2',
  'transferInventoryUnitsAdmin',
  'processToyScheduleOrders',
];

const SILENT_ERROR_OPERATIONS = [
  'CheckInventoryUnitAssignment',
  'PackInventoryUnit',
  'MarkInventoryUnitDamaged',
  'PreviewInventoryUnitInspection',
  'ConfirmReturnOrderV2',
  'TransferInventoryUnitsAdmin',
  'UltraPlanRoadmap',
];

export const cacheData = new InMemoryCache({
  typePolicies: {
    InventoryUnitInspectionFormChecklistItem: { keyFields: false },
    InventoryUnitInspectionFormContent: { keyFields: false },
    InventoryUnitInspectionForm: { keyFields: false },
  },
});

const httpLink = createHttpLink({
  uri: process.env?.REACT_APP_SERVER_URL,
});

const toast = ({ message: content, type, duration }) => {
  message?.destroy();
  switch (type) {
    case 'info':
      message?.info(content, duration);
      break;
    case 'success':
      message?.success(content, duration);
      break;
    case 'warning':
      message?.warning(content, duration);
      break;
    case 'error':
      message?.error(content, duration);
      break;
    default:
      break;
  }
};

const authLink = setContext((ctx, { headers }) => {
  // eslint-disable-next-line no-undef
  const userToken = localStorage.getItem(TOKEN);
  let newHeaders = {
    ...(headers || {}),
    'x-from-web': 'true',
    'x-platform-type': PLATFORM_TYPE,
  };

  if (!headers?.Authorization) {
    newHeaders = {
      ...newHeaders,
      Authorization: userToken ? `Bearer ${userToken}` : '',
    };
  }
  return {
    headers: newHeaders,
  };
});

const responseMessageLink = new ApolloLink((operation, forward) =>
  forward(operation)?.map((response) => {
    const { data } = response;
    if (
      data &&
      isObject(data) &&
      Object?.keys(data)?.length > 0 &&
      data?.[`${Object?.keys(data)?.[0]}`]?.message &&
      !SILENT_MESSAGE_OPERATIONS?.includes(Object?.keys(data)?.[0])
    ) {
      if (Object?.keys(data)?.[0] === 'forgotUserPassword') {
        if (data?.[`${Object?.keys(data)?.[0]}`]?.status !== 'ERROR') {
          setTimeout(() => {
            toast({
              message:
                data?.[`${Object?.keys(data)?.[0]}`]?.message ||
                'Operation successful',
              type: 'success',
            });
          }, 1000);
        }
      } else if (
        [
          'orderChallanExport',
          'orderChallanExportAdmin',
          'orderItemStickerExport',
          'orderItemStickerExportAdmin',
        ]?.includes(Object?.keys(data)?.[0])
      ) {
        setTimeout(() => {
          const oResponse = data?.[`${Object?.keys(data)?.[0]}`];

          if (!response) {
            return;
          }

          toast({
            message: oResponse?.message || 'Operation successful',
            type: oResponse?.status === 'ERROR' ? 'error' : 'success',
            duration: 8,
          });
        }, 1000);
      } else {
        setTimeout(() => {
          const oResponse = data?.[`${Object?.keys(data)?.[0]}`];

          if (!response) {
            return;
          }

          toast({
            message: oResponse?.message || 'Operation successful',
            type: oResponse?.status === 'ERROR' ? 'error' : 'success',
          });
        }, 1000);
      }
    }
    return response;
  }),
);

const errorLink = onError((options) => {
  const { graphQLErrors, networkError, response, operation } = options;
  const isSilentErrorOperation = SILENT_ERROR_OPERATIONS?.includes(
    operation?.operationName,
  );

  if (networkError?.statusCode === 405) {
    if (disableToastTimeout) {
      clearTimeout(disableToastTimeout);
    }

    disableToastTimeout = setTimeout(() => {
      if (networkError?.result?.message) {
        toast({
          message: networkError?.result?.message,
          type: 'error',
        });
      }
    }, 200);

    history?.replace(ROUTES?.LOGOUT);
    return;
  }

  if (graphQLErrors?.length > 0) {
    const isForBidden = graphQLErrors?.[0]?.extensions?.code === 'FORBIDDEN';
    const skipToastCodes = operation?.getContext?.()?.skipToastCodes || [];
    const isHandledByCaller = skipToastCodes?.includes(
      graphQLErrors?.[0]?.extensions?.code,
    );

    if (!isForBidden && !isSilentErrorOperation && !isHandledByCaller) {
      setTimeout(() => {
        toast({
          message: graphQLErrors?.[0]?.message,
          type: 'error',
        });
      }, 1000);
    }
  } else if (!isSilentErrorOperation) {
    setTimeout(() => {
      toast({
        message: 'Something went wrong!',
        type: 'error',
      });
    }, 1000);
  }

  if (response) {
    response?.errors?.map((error) => {
      const { message: errorMessage, locations, path, extensions } = error;

      // Enable when sentry integrated
      Sentry?.captureException(
        new Error(
          `[Response error]: Message: ${errorMessage}, Location: ${locations}, Path: ${path}`,
        ),
      );
      // commenting temporarily as it is not required
      // if (extensions?.code === 'USER_NOT_FOUND') {
      //   history?.replace(`${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}`);
      // }
      if (extensions?.code === 'SESSION_EXPIRED') {
        history?.replace(ROUTES?.AUTHENTICATION);
      }
      if (extensions?.code === 'FORBIDDEN') {
        history?.replace('/access-denied');
      }

      if (
        extensions?.code === 'UNAUTHENTICATED' ||
        extensions?.code === 405 ||
        extensions?.code === 'INVALID_TOKEN' ||
        extensions?.exception?.name === 'JsonWebTokenError'
      ) {
        history?.replace(ROUTES?.LOGOUT);
      }

      // eslint-disable-next-line no-console
      return console?.log(
        `[Response error]: Message: ${errorMessage}, Location: ${locations}, Path: ${path}`,
      );
    });
  }

  if (networkError) {
    // eslint-disable-next-line no-console
    console?.log(`[Network error]: ${networkError}`);
    Sentry?.captureException(new Error(`[Network error]: ${networkError}`));
  }
});

const client = new ApolloClient({
  cache: cacheData,
  link: from([responseMessageLink, errorLink, authLink, httpLink]),
});

export default client;
